import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, EntitiesService, GenesisUnitOfWork, InvoiceService, OrderService, UtilityService } from 'app/core/core.module';
import { Guid } from 'app/core/data/entities';
import { ReportService } from 'app/core/data/report.service';
import {
    Confirmation,
    Dealer,
    EmailMessage,
    Invoice,
    InvoiceLine,
    Order,
    OrderLine,
    OrderLineDetail,
    OrderPaymentMethod,
    OrderType,
    Part,
    PartType,
    Payment,
    PaymentCredit,
    PaymentCreditType,
    PaymentLine,
    PricingTier,
    SalesRep,
    Setting,
    ShipAddress,
} from 'app/core/model/entity-model';
import { Utility } from 'app/core/utility';
import { DealerDetailComponent } from 'app/dealers/dealer-detail/dealer-detail.component';
import { PartDetailComponent } from 'app/parts/part-detail/part-detail.component';
import { PartNumberDialogComponent } from 'app/parts/part-number-dialog/part-number-dialog.component';
import { PartSearchComponent } from 'app/parts/part-search/part-search.component';
import { AuditDialogComponent } from 'app/shared/audit-dialog/audit-dialog.component';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm, custom } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import { environment } from 'environments/environment';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AchDialogComponent } from '../ach-dialog/ach-dialog.component';
import { ImportInvoiceLinesDialogComponent } from '../import-invoice-lines-dialog/import-invoice-lines-dialog.component';
import { MiscLineDialogComponent } from '../misc-line-dialog/misc-line-dialog.component';
import { OrderBuildDetailComponent } from '../order-build-detail/order-build-detail.component';
import { OrderShipAddressDialogComponent } from '../order-ship-address-dialog/order-ship-address-dialog.component';

@Component({
    selector: 'app-order-detail',
    templateUrl: './order-detail.component.html',
    styleUrls: ['./order-detail.component.scss'],
    standalone: false,
})
export class OrderDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    order: any;
    orderLines: any;
    preSubmittedNote: string;
    orderId: any;
    dealers: Dealer[];
    paymentMethods: OrderPaymentMethod[];
    deliveryOptions: any;
    orderTypes: OrderType[];
    shipAddresses: ShipAddress[];
    readOnly = true;
    canChangeShipAddress = true;
    orderAttachments: any[];
    uploadHeader: any;
    uploadUrl = (environment.serviceName + '/file/SaveOrderAttachmentToBlobStorage') as string;

    ready = false;
    unitOfMeasures: any[];
    shipCountryIsUS = true;
    shipCountry: any;
    countries = ['US', 'OTHER'];
    states: any;
    userAccess = false;
    currentUser: any;
    readyToDeliver = false;
    processing = false;
    showAsDialog = false;

    isToastVisible = false;
    validationGroup = 'mainValidationGroup';
    errorAction = 'saving';

    orderLinesVisible: any;
    saving = false;
    title: any;
    calculatedFreight: number = 0;
    selectedShipAddress: ShipAddress;
    mileageRate: number = 0;
    discountMileageRate: number = 0;
    buildLeadTime: number;
    orderLeadTime: number;
    allowFreightEntry = false;
    confirmations: Confirmation[];
    dealerConfirmations: Confirmation[];
    pricingTiers: PricingTier[];
    selectablePricingTiers: PricingTier[];

    canSeeSales = false;
    canSeePricingTier = false;
    canApplyACH = false;
    canUnApplyACH = false;
    canSeeACH = false;
    canUpdatePricing = false;
    canUnSubmit = false;
    canEditSubmittedOrders = false;
    canChangeSalesRep = false;
    dealerTotal = 0;
    achInfo: any;

    startWarranty = false;
    isWarranty = false;
    warrantyOriginalOrder: number;
    warrantyVIN: string;
    isWinterOrder = false;
    orderEditedAfterSubmit: boolean = false;
    orderLineCanBeSubmitted: boolean = false;
    isRetail = false;
    isQuote = false;
    startQuote = false;
    startCredit = false;

    isCreditOrder = false;
    invoiceCreditLines: any[];
    invoice: Invoice;
    creditReason: string;
    paymentLines: PaymentLine[];

    Statuses = Order.Statuses;
    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        protected location: Location,
        private entitiesService: EntitiesService,
        private reportService: ReportService,
        private utility: UtilityService,
        private orderService: OrderService,
        private invoiceService: InvoiceService,
    ) {}

    get dealerDeliveryConfirmationEnabled(): boolean {
        return this.appInfo.getDealerDeliveryConfirmationEnabled();
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges() && !this.orderEditedAfterSubmit;
    }
    ngOnDestroy() {
        this.uow.rollback();
    }

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };

        this.orderTypes = this.uow.lookups.orderTypes.filter((type) => type.id !== Order.Types.InvoiceCredit && type.id !== Order.Types.Credit);
        this.deliveryOptions = this.uow.lookups.deliveryOptions;
        this.paymentMethods = (await this.uow.orderPaymentMethods.whereActive('displayOrder')) as OrderPaymentMethod[];
        this.states = this.uow.lookups.states;
        const salesReps = this.uow.lookups.salesReps;
        this.dealers = await this.uow.getDealers();
        this.mileageRate = ((await this.uow.getSettingsByName('MileageRate')) as any).decimalValue;
        this.discountMileageRate = ((await this.uow.getSettingsByName('DiscountMileageRate')) as any).decimalValue;
        this.buildLeadTime = ((await this.uow.getSettingsByName('BuildLeadTime')) as Setting).intValue;
        this.orderLeadTime = ((await this.uow.getSettingsByName('LeadTime')) as Setting).intValue;

        this.shipAddresses = [];
        if (!this.orderId) this.orderId = this.route.snapshot.params.id;

        this.startWarranty = this.route.snapshot.queryParams.warranty;
        if (this.startWarranty) {
            this.warrantyOriginalOrder = this.route.snapshot.queryParams.originalOrderId;
            this.warrantyVIN = this.route.snapshot.queryParams.vin;
        }

        this.startQuote = this.route.snapshot.queryParams.quote;
        this.startCredit = this.route.snapshot.queryParams.credit;

        await this.getData();

        const allPricingTiers = this.uow.lookups.pricingTiers;
        this.pricingTiers = allPricingTiers.filter((tier) => !tier.inactive);

        if (this.order.statusId === Order.Statuses.WaitingOnConfirmation || this.order.statusId === Order.Statuses.InProcess || this.order.statusId === Order.Statuses.Complete) {
            const isInActiveTiers = _.find(this.pricingTiers, { id: this.order.pricingTierId });
            if (this.order.pricingTierId && !isInActiveTiers) {
                const currentTier = _.find(allPricingTiers, { id: this.order.pricingTierId });
                if (currentTier) {
                    this.pricingTiers = [currentTier];
                    console.log('current tier', currentTier);
                }
            }
        }
        // ? this.uow.lookups.pricingTiers
        // : this.uow.lookups.pricingTiers.filter((tier) => !tier.inactive);

        this.calcFreight();
        this.ready = true;
    }

    print() {
        this.reportService.openSalesReportWithGuid([this.order.orderGuid]);
    }

    async getData() {
        this.appInfo.pleaseWaitShow();
        if (this.orderId === 'new' || this.orderId === 'invoice-credit') {
            this.order = this.uow.orders.createEntity() as Order;
            this.order.typeId = this.startWarranty
                ? Order.Types.Warranty
                : this.startQuote
                  ? OrderType.Types.Quote
                  : this.startCredit
                    ? Order.Types.Credit
                    : Order.Types.SalesOrder;
            this.order.statusId = Order.Statuses.New;
            if (this.orderId === 'new') this.order.invoiceNumber = await this.utility.getNextSalesOrderNumber();
            this.order.createdById = this.auth.currentUser.id;
            this.order.createDate = new Date();
            this.order.createdTimestamp = new Date();
            this.order.deliveryOptionString = 'TBD';
            this.order.pONumber = 'Per Email';
            this.order.orderGuid = Utility.generateUUID();
            if (this.startWarranty) await this.copyWarrantyOrderFields();

            if (this.order.typeId === Order.Types.InvoiceCredit || this.order.typeId === Order.Types.Credit) {
                this.order.deliveryOptionId = Order.DeliveryOptions.TBD;
                this.order.paymentMethod = OrderPaymentMethod.Methods.InvoiceDealer;
            }

            if (this.orderId === 'invoice-credit') {
                console.log('invoice-credit-lines', this.invoiceCreditLines);
                console.log('paymentLines', this.paymentLines);
                this.order.typeId = Order.Types.InvoiceCredit;
                this.order.dealerId = this.invoice.dealerId;
                this.order.note = `Invoice Credit created from Invoice #${this.invoice.invoiceNumber}\r\nReason: ${this.creditReason}`;
                this.addInvoiceCreditLines();
            }
        } else {
            this.order = (await this.uow.orders.byId(
                this.orderId,
                'orderLines.part.partPartTypes, dealer.shipAddresses, orderLines.orderLineDetails.status, orderLines.orderLineDetails.paymentLines, orderLines.returnedDetail.invoice, orderAttachments',
            )) as Order;
            this.clearDisplayOrder();
            this.shipAddresses = !!this.order.dealer ? this.order.dealer.shipAddresses : [];
            this.order.shipCompanyName = this.order.dealer ? this.order.dealer.companyName : '';
        }

        this.isWarranty = this.order.typeId === OrderType.Types.Warranty || this.startWarranty;
        this.isWinterOrder = this.order.typeId === OrderType.Types.Winter;
        this.isRetail = this.order.typeId === OrderType.Types.PartsOrder;
        this.preSubmittedNote = this.order.note;

        this.getDataOrderLines();
        await this.getOrderAttachments();
        if (!this.showAsDialog) this.setTitle();
        this.setPermissions();
        this.calcTotals();
        await this.getConfirmations();
        await this.getAchPayments();
        if (!this.readOnly) await this.getDealerTotal();
        this.order.getTierBySales(this.pricingTiers);
        this.appInfo.pleaseWaitHide();
        console.log('order', this.order);
        console.log('orderLines', this.orderLines);
    }

    addInvoiceCreditLines() {
        //     addOrderLine(part?: any, quantity?: number, parentLineId?: number, order?: any, mode?: any) {

        this.invoiceCreditLines.forEach((line) => {
            this.addOrderLine(line.orderLineDetail?.orderLine?.part, line.quantity * -1, null, this.order, 'credit', line.price);
            // const orderLine = this.uow.orderLines.createEntity({
            //     orderId: this.order.id,
            //     partNumber: line.partNumber,
            //     description: line.description,
            //     quantity: line.quantity * -1,
            //     price: line.price,
            //     isCredit: true,
            //     created: new Date(),
            //     createdBy: this.auth.currentUser.userName,
            // }) as OrderLine;
        });
    }

    clearDisplayOrder() {
        this.order.orderLines.forEach((line) => {
            line._displayOrder = null;
        });
    }

    async copyWarrantyOrderFields() {
        const originalOrder = (await this.uow.orders.byId(
            this.warrantyOriginalOrder,
            'orderLines.part, dealer.shipAddresses, orderLines.orderLineDetails.status, orderAttachments',
        )) as any;
        this.shipAddresses = !!originalOrder.dealer ? originalOrder.dealer.shipAddresses : [];
        this.order.pONumber = 'WARRANTY';
        this.order.dealer = originalOrder.dealer;
        this.order.shipToLocation = originalOrder.shipToLocation;
        this.order.shipToAddress1 = originalOrder.shipToAddress1;
        this.order.shipToAddress2 = originalOrder.shipToAddress2;
        this.order.shipToCity = originalOrder.shipToCity;
        this.order.shipToState = originalOrder.shipToState;
        this.order.shipToZip = originalOrder.shipToZip;
        this.order.shipToCountry = originalOrder.shipToCountry;
        this.order.shipToContactName = originalOrder.shipToContactName;
        this.order.shipToPhone = originalOrder.shipToPhone;
        this.order.shipToEmail = originalOrder.shipToEmail;
        this.order.note = `Original Invoice #${originalOrder.invoiceNumber}\r\nVIN: ${this.warrantyVIN}`;
    }

    async getAchPayments() {
        const achList = [];
        const achs = (await this.uow.getACHPaymentByOrderId(this.order.id)) as any;
        achs.forEach((ach) => {
            achList.push(`ACH Pricing Applied: ${moment(ach.transactionDate).format('MM/DD/YYYY')}`);
        });
        this.achInfo = achList.join('<br>');
    }

    getDataOrderLines() {
        this.orderLines = this.order.orderLines.filter((orderLine) => {
            return !orderLine.parentLineId || orderLine.isOption; //&& !orderLine.deleted;
        }) as OrderLine[];
        this.orderLines.forEach((ol) => {
            ol.entityPartPrice = ol.originalPrice;
        });
    }

    async getConfirmations() {
        this.confirmations = (await this.uow.getConfirmationsByOrderId(this.order.id, true)) as Confirmation[];
        this.dealerConfirmations = this.confirmations.filter((confirmation) => {
            return !confirmation.salesRepConfirmation;
        });
    }

    async getDealerTotal() {
        if (!this.order.dealerId) return;

        let tier;
        const dealerTotal = await this.uow.getDealerYTDTierQualifyingSales(this.order.dealerId);

        //check if the dealer has a Tier Override set
        if (this.order.dealer.pricingTierId) {
            tier = _.find(this.pricingTiers, (x) => x.id === this.order.dealer.pricingTierId);
        } else {
            if (this.order.dealerId === Dealer.DealerOptions.Miscellaneous || this.order.dealerId === Dealer.DealerOptions.GenesisStock)
                tier = _.find(this.pricingTiers, (x) => x.name.toLowerCase() === 'retail');
            else {
                const dealerSalesTier = await this.uow.getDealerTierBasedOnQualifyingSales(this.order.dealerId);
                tier = _.find(this.pricingTiers, (x) => {
                    return x.id === dealerSalesTier;
                });
            }
        }

        if (!this.order.pricingTierId) {
            this.order.currentYTDSales = dealerTotal ?? 0;
            if (!!tier) {
                this.order.pricingTierId = tier.id;
            }
        }

        this.order.getTierBySales(this.pricingTiers);
    }

    async updateRetailPrice() {
        this.orderLines.forEach((ol) => {
            const part = ol.part as Part;
            if (part) {
                ol.price = part.retailPrice;
                // console.log('orderLineprice', ol.price)
            }
        });
    }

    async updatePricingTier() {
        this.orderLines.forEach((ol) => {
            this.setLinePricing(ol);
        });
    }

    hasChanges() {
        return this.uow.hasChanges();
    }
    setTitle() {
        if (this.orderId === 'new') this.title = this.order.typeId === Order.Types.Credit ? 'Credit' : 'Sales Order Detail - New';
        else {
            this.title = `${this.order.dealer?.companyName}# ${this.order.invoiceNumber}`;
        }
        this.appInfo.title = this.title;
    }
    setPermissions() {
        if (this.order.typeId === Order.Types.InvoiceCredit || this.order.typeId === Order.Types.Credit) this.orderTypes = this.uow.lookups.orderTypes;

        this.readOnly = this.order.statusId === Order.Statuses.InProcess || this.order.statusId === Order.Statuses.Complete || this.showAsDialog;
        this.canUpdatePricing = !this.readOnly || this.auth.hasRole('SalesOrder:CanUpdatePricingAfterSubmit');
        this.canChangeShipAddress = this.order.statusId !== Order.Statuses.Complete;
        this.canSeeSales = this.auth.hasRole('SalesOrder:CanSeeDealerSales');
        this.canSeePricingTier = this.auth.hasRole('SalesOrder:CanSeePricingTier') || true;
        this.canApplyACH = (this.order.statusId === Order.Statuses.InProcess || this.order.statusId === Order.Statuses.Complete) && !this.showAsDialog;
        this.canUnApplyACH = this.auth.hasRole('SalesOrder:CanUnApplyACH') && !this.showAsDialog;
        this.canUnSubmit = this.auth.hasRole('SalesOrder:CanUnSubmit') && !this.showAsDialog;
        this.canChangeSalesRep = this.auth.hasRole('SalesOrder:CanChangeSalesRep');
        const type = _.find(this.orderTypes, { id: this.order.typeId });
        console.log('type', type, this.order.typeId);
        this.canSeeACH = type && !type.taxable && type.id !== OrderType.Types.Winter;
        this.isWinterOrder = type?.id === OrderType.Types.Winter;
        this.orderEditedAfterSubmit = this.order.statusId === Order.Statuses.InProcess && _.some(this.order.orderLines, { submitted: null, deleted: null });
        this.canEditSubmittedOrders = this.auth.hasRole('SalesOrder:CanEditSubmittedOrders') && !this.showAsDialog;
        this.isQuote = type?.id === OrderType.Types.Quote;
        this.isCreditOrder = type?.id === Order.Types.InvoiceCredit || type?.id === Order.Types.Credit;
    }

    onTypeSelectionChange(e) {
        if (e) {
            if (e.value === OrderType.Types.PartsOrder) {
                this.order.dealerId = Dealer.DealerOptions.Miscellaneous;
                this.updateRetailPrice();
            } else this.updatePricingTier();
            const type = _.find(this.orderTypes, { id: e.value });
            this.setPermissions();
            // this.canSeeACH = type && !type.taxable && type.id !== OrderType.Types.Winter;
            // this.isWinterOrder = type.id === OrderType.Types.Winter;
            this.isRetail = type.id === OrderType.Types.PartsOrder;
        }
        this.calcTotals();
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    onOrderLineSelectionChanged(e) {
        if (!e) this.orderLineCanBeSubmitted = false;

        if (e && !!e.selectedRowsData) {
            const row = e.selectedRowsData[0];
            if (row) this.orderLineCanBeSubmitted = !row.submitted || _.some(row.children, { submitted: null });
        }
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.deleted) e.rowElement.classList.add('deleted-row');
            e.cells.forEach((cell) => {
                if (cell.column.dataField === 'part.retailPrice' || cell.column.dataField === 'originalPrice' || (!this.achInfo && cell.column.dataField === 'achLineAmount')) {
                    cell.cellElement.className = cell.cellElement.className + ' retail-price';
                }
                if (!!this.achInfo) {
                    if (cell.column.dataField === 'achLineAmount') {
                        if (!!cell.row.data.paidAch) cell.cellElement.className = cell.cellElement.className + ' ach-price';
                        else cell.cellElement.className = cell.cellElement.className + ' retail-price';
                    }
                    if (cell.column.dataField === 'price') {
                        if (!!cell.row.data.paidAch) cell.cellElement.className = cell.cellElement.className + ' retail-price';
                        else cell.cellElement.className = cell.cellElement.className + ' not-include-in-ach-price';
                    }
                }
            });
        }
    }

    async onRowUpdating(e) {
        // 3/8/23 MR - commenting out after conversation with heather, more one off pricing changes then product pricing changes
        // if (!!e.data.price && e.data.price !== e.data.entityPartPrice) {
        //     const result = await (confirm('Do you want to update price on the master part as well?', `${this.appInfo.settings.company}`) as Promise<boolean>);
        //     if (!!result) {
        //         this.appInfo.pleaseWaitShow();
        //         const part = (await this.uow.parts.byId(e.data.partId)) as any;
        //         part.price = e.data.price;
        //         part.note = `Part Price updated by ${this.auth.currentUser.userName} for order ${this.order.invoiceNumber} on ${moment().toDate()}\r\n${
        //             !!part.note ? part.note : ''
        //         }`;
        //         e.data.entityPartPrice = e.data.price;
        //         await this.uow.commit();
        //         this.appInfo.pleaseWaitHide();
        //     }
        // }
        this.calcFreight();
        this.calcTotals();
    }

    async onRowDoubleClick(e) {
        // console.log(e);
        if (!e.data || !e.data.id || !e.data.partId || this.showAsDialog) return;

        const orderId = this.order.id;
        if (!orderId) return;
        const modalRef = this.modalService.show(PartDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partId: e.data.partId,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                // await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    locationDisplay = (e) => {
        return !!e ? (e.location ? e.location : !!e.city ? `${e.city}, ${e.state}` : 'Location Field On Dealer Not Set') : null;
    };

    async dealerSelectionChanged(e) {
        this.appInfo.pleaseWaitShow();
        this.order.pricingTierId = null;

        const paymentMethodId = this.orderService.getPaymentMethodId(this.order.dealer?.dealerTermId);
        this.order.paymentMethod = paymentMethodId ? this.paymentMethods.find((method) => method.id === paymentMethodId)?.name : null;

        this.order.dealer = await this.uow.dealers.byId(this.order.dealerId, 'shipAddresses');
        this.shipAddresses = this.order.dealer.shipAddresses;
        if (!!this.order.dealer.shipSameAsBilling || !this.order.dealer.shipAddresses || this.order.dealer.shipAddresses.length === 0) {
            this.clearShipping();
            this.copyBilling();
        } else if (!!this.order.dealer.shipAddresses && this.order.dealer.shipAddresses.length === 1) {
            this.order.shipAddressId = this.order.dealer.shipAddresses[0].id;
            this.clearShipping();
            await this.copyShipping();
        } else if (!!this.order.dealer.shipAddresses && this.order.dealer.shipAddresses.length > 1) {
            this.order.shipAddressId = null;
            this.clearShipping();
        }
        // if (this.order.dealer.freight) this.order.freight = this.order.dealer.freight;
        this.order.colorCode = this.order.dealer.colorCode;
        this.order.printPricing = this.order.dealer.salesOrderPricingVisible;
        this.order.noPrePay = this.order.dealer.noPrePay;
        this.order.salesRepId = this.order.dealer.salesRepId;
        await this.getDealerTotal();
        this.calcFreight();
        this.calcTotals();
        this.appInfo.pleaseWaitHide();
    }

    pricingTierSelectionChanged(e) {
        const previousPricingTierId = e.previousValue;

        // !e.event checks for non-user triggered updates to prevent this method from triggering itself
        if (this.order.statusId === Order.Statuses.New || !e.event) {
            this.updateAfterPricingSelectionChange();
            return;
        }

        const previousTierName = _.find(this.pricingTiers, { id: previousPricingTierId })?.name || 'None';

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Pricing Tier Changed',
                dialogPrompt: 'Why is the pricing tier changing after a confirmation was sent?',
                mode: 'textarea',
                selectionRequired: true,
            },
        });

        modalRef.content.result.subscribe((result) => {
            modalRef.hide();
            if (!result.result) {
                this.order.pricingTierId = previousPricingTierId;
                return;
            }

            this.updateAfterPricingSelectionChange();
            const newTierName = this.order.pricingTier ? this.order.pricingTier.name : 'None';
            const changeNote = `Pricing Tier Change by ${this.auth.currentUser.userName}: ${previousTierName} -> ${newTierName} - ${result.value}`;
            this.order.note = this.order.note && this.order.note.length > 0 ? `${this.order.note}\n${changeNote}` : changeNote;
        });
    }

    updateAfterPricingSelectionChange() {
        this.updatePricingTier();
        this.order.discountName = this.order.pricingTier ? this.order.pricingTier.name : null;
        this.order.discountPercentage = this.order.pricingTier ? this.order.pricingTier.percentage : 0;
        this.calcTotals();
    }

    deliveryOptionSelectionChanged(e) {
        this.calcFreight();
    }

    freightChange = (e) => {
        this.calcTotals();
        // tslint:disable-next-line:semicolon
    };

    calcLoadedMiles(): number {
        if (!!this.order.dealer && !!this.order.shipAddressId) {
            const shipAddress = _.find(this.shipAddresses, { id: this.order.shipAddressId }) as any;
            if (shipAddress) return shipAddress.loadedMiles;
            else return 0;
        } else if (!!this.order.dealer && this.order.dealer.loadedMiles) {
            return this.order.dealer.loadedMiles;
        } else return 0;
    }

    calcFreight() {
        if (this.order.statusId == Order.Statuses.Canceled || this.order.statusId == Order.Statuses.Complete || this.order.statusId == Order.Statuses.Paid) return;
        const deliveryId = this.order.deliveryOptionId;
        if (!deliveryId) {
        } else if (deliveryId === Order.DeliveryOptions.CPU || deliveryId === Order.DeliveryOptions.DPU) {
            this.allowFreightEntry = false;
            this.calculatedFreight = 0;
            this.order.freight = 0;
        } else if (deliveryId === Order.DeliveryOptions.UPS) {
            if (this.calculatedFreight !== this.calcLoadedMiles() * this.mileageRate) this.calculatedFreight = this.calcLoadedMiles() * this.mileageRate;
            this.allowFreightEntry = true;
        } else if (deliveryId === Order.DeliveryOptions.Genesis || deliveryId === Order.DeliveryOptions.TBD) {
            if (this.calculatedFreight !== this.calcLoadedMiles() * this.mileageRate) this.calculatedFreight = this.calcLoadedMiles() * this.mileageRate;
            this.order.freight = 0;
            this.allowFreightEntry = false;
        }
    }
    copyFreight() {
        this.order.freight = this.calculatedFreight;
    }

    async copyBilling() {
        if (!this.order || !this.order.dealerId) return;
        this.order.shipCompanyName = this.order.dealer.companyName;
        this.order.shipToContactName = this.order.dealer.contactName;
        this.order.shipToLocation = null;
        this.order.shipToAddress1 = this.order.dealer.billAddress1;
        this.order.shipToAddress2 = this.order.dealer.billAddress2;
        this.order.shipToCity = this.order.dealer.billCity;
        this.order.shipToState = this.order.dealer.billState;
        this.order.shipToZip = this.order.dealer.billZip;
        this.order.shipToCountry = this.order.dealer.billCountry;
        this.order.shipToPhone = this.order.dealer.phone1;
        this.order.shipToEmail = null;
        if (!this.order.shippingNote || this.order.shippingNote.trim() === '') {
            this.order.shippingNote = this.order.dealer.shippingNote;
        }
    }
    async copyShipping(e?) {
        if (!this.order.shipAddressId) return;
        this.selectedShipAddress = (await this.uow.shipAddresses.byId(this.order.shipAddressId)) as ShipAddress;
        const selectedShipAddress = this.selectedShipAddress;

        this.order.shipCompanyName = this.order.dealer.companyName;
        this.order.shipToContactName = selectedShipAddress.contactName;
        this.order.shipToLocation = selectedShipAddress.location;
        this.order.shipToAddress1 = selectedShipAddress.address1;
        this.order.shipToAddress2 = selectedShipAddress.address2;
        this.order.shipToCity = selectedShipAddress.city;
        this.order.shipToState = selectedShipAddress.state;
        this.order.shipToZip = selectedShipAddress.zip;
        this.order.shipToCountry = selectedShipAddress.country;
        this.order.shipToPhone = selectedShipAddress.phone1;
        this.order.shipToEmail = selectedShipAddress.email;
        this.order.shippingNote = selectedShipAddress.notes;
        this.calcFreight(); //runs when the ship address dropdown changes
    }
    async clearShipping() {
        this.order.shipCompanyName = null;
        this.order.shipToContactName = null;
        this.order.shipToLocation = null;
        this.order.shipToAddress1 = null;
        this.order.shipToAddress2 = null;
        this.order.shipToCity = null;
        this.order.shipToState = null;
        this.order.shipToZip = null;
        this.order.shipToCountry = null;
        this.order.shipToPhone = null;
        this.order.shipToEmail = null;
        this.order.shippingNote = null;
    }
    async addDealer() {
        // const success = await this.save();
        // if (!success) return;

        const modalRef = this.modalService.show(DealerDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                // order: this.order,
                showAsDialog: true,
                key: 'new',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.order.dealerId = result.dealer.id;
                // await this.save();
            }
            // re-get dealer info in case they edited the dealer or addresses
            const dealer = (await this.uow.dealers.byId(this.order.dealerId, 'shipAddresses')) as Dealer;
            this.dealers.splice(this.dealers.indexOf(dealer), 1, dealer);
            // forces dropdowns to refresh by setting their arrays
            this.dealers = this.dealers.slice();
            this.shipAddresses = dealer.shipAddresses.slice();
            modalRef.hide();
        });
    }

    async editShipAddress() {
        const success = await this.save();
        if (!success) return;

        const modalRef = this.modalService.show(OrderShipAddressDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                order: this.order,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                await this.save();
            }
            // re-get dealer info in case they edited the dealer or addresses
            const dealer = (await this.uow.dealers.byId(this.order.dealerId, 'shipAddresses')) as Dealer;
            this.dealers.splice(this.dealers.indexOf(dealer), 1, dealer);
            // forces dropdowns to refresh by setting their arrays
            this.dealers = this.dealers.slice();
            this.shipAddresses = dealer.shipAddresses.slice();
            modalRef.hide();
        });
    }
    shipCountrySelectionChange(e) {
        if (!!this.order) {
            this.shipCountryIsUS = this.shipCountry === 'US';
            this.order.shipToCountry = this.shipCountry === 'US' ? 'US' : this.order.shipToCountry;
        }
    }
    calcTotals() {
        if (!this.order) return;
        if (this.order.statusId == Order.Statuses.Complete || this.order.statusId == Order.Statuses.Paid || this.order.statusId == Order.Statuses.Canceled) return;
        console.log('calcTotal');
        const freightFixed = this.order.freight.toFixed(2);
        const subTotalFixed = this.order.subTotal.toFixed(2);
        this.calcTax(subTotalFixed);
        const taxFixed = this.order.type.taxable ? this.order.tax.toFixed(2) : 0;
        const totalFixed = this.order.total.toFixed(2);

        if (this.order.deliveryOptionId === Order.DeliveryOptions.UPS) {
            if (totalFixed !== subTotalFixed + freightFixed + taxFixed) this.order.total = this.order.subTotal + this.order.freight + this.order.tax;
        } else {
            if (this.order.total !== this.order.subTotal + taxFixed) this.order.total = this.order.subTotal + this.order.tax;
        }
        if (!this.order.achTotal || this.order.achTotal.toFixed(2) !== this.order.achDiscountAmount.toFixed(2)) this.order.achTotal = this.order.achDiscountAmount;
    }

    calcTax(subTotalFixed: number) {
        let tax = 0;
        console.log('calcTax');

        if (this.orderLines && this.orderLines.length > 0) {
            this.orderLines.forEach((line) => {
                line.tax = Utility.calcLineTax(line, line.part?.partPartTypes, this.order.type);
                tax += line.tax;
            });
        }
        if (this.order.tax !== Utility.round(tax, 2)) this.order.tax = Utility.round(tax, 2);
    }

    async removeOrderLine() {
        if (this.linesGrid.instance.getSelectedRowsData().length === 0 || this.processing) return;
        const selectedLine = this.linesGrid.instance.getSelectedRowsData()[0];

        if (selectedLine.isOption || !!selectedLine.parentLineId) {
            (<OrderLine>selectedLine).delete(false);
        } else {
            this.linesGrid.instance.getSelectedRowsData().forEach((row) => {
                // recursively delete
                (<OrderLine>row).delete(true);
            });
        }
        this.processing = true;
        await this.uow.commit();
        this.getDataOrderLines();
        this.appInfo.pleaseWaitShow();
        this.order.updateDisplayOrder();
        this.clearDisplayOrder();
        this.calcFreight();
        this.calcTotals();
        await this.uow.commit();
        // this.getDataOrderLines();

        this.appInfo.pleaseWaitHide();
        this.processing = false;
    }

    async removeOrderLineAfterSubmit() {
        const selectedLine = this.linesGrid.instance.getSelectedRowsData()[0];
        if (!selectedLine) return;

        //get the selected line detail and children
        const details = (await this.uow.getOrderLineDetailsByOrderLineId(
            selectedLine.id,
            'orderLine, optionDetails.invoiceLines, invoiceLines, optionDetails.orderLine',
        )) as OrderLineDetail[];

        //check that all the details and options are not built
        const allDetailsNotBuilt = details.every((x) => x.built === null || x.orderLine.isNonManufactured);
        let optionCheck = true;
        details.forEach((detail) => {
            detail.optionDetails.forEach((option) => {
                if (option.built && !option.orderLine.isNonManufactured) optionCheck = false;
            });
        });
        if (!allDetailsNotBuilt || !optionCheck) {
            alert(`One or more of the details has been built already. Must remove from the Build Detail screen`, this.appInfo.settings.company);
            return;
        }

        //check that all the details and options are not Invoiced
        const allDetailsNotPaid = details.filter((x) => x.invoiceLines.filter((il) => !il.deleted).length > 0).length === 0;
        let optionPaidCheck = true;
        details.forEach((detail) => {
            detail.optionDetails.forEach((option) => {
                if (option.invoiceLines.filter((il) => !il.deleted).length > 0) optionPaidCheck = false;
            });
        });

        if (!allDetailsNotPaid || !optionPaidCheck) {
            alert(`One or more of the details is on an INVOICE already.  Must remove from Invoice before removing from Sales Order`, this.appInfo.settings.company);
            return;
        }

        const confirmResult = await confirm(`Are you sure you want to remove this line?  This cannot be undone.  Continue?`, this.appInfo.settings.company);
        if (!confirmResult) return;

        this.appInfo.pleaseWaitShow();
        this.processing = true;
        const result = await this.utility.removeOrderLineAfterSubmitted(selectedLine.id);

        selectedLine.deleted = new Date();
        selectedLine.deletedBy = this.auth.currentUser.userName;

        this.orderLines.forEach((line) => {
            if (line.parentLineId === selectedLine.id) {
                line.deleted = new Date();
                line.deletedBy = this.auth.currentUser.userName;
            }
        });

        await this.uow.commit();
        this.getDataOrderLines();
        this.order.updateDisplayOrder();
        this.clearDisplayOrder();
        this.calcFreight();
        this.calcTotals();
        await this.uow.commit();
        // this.getDataOrderLines();

        this.appInfo.pleaseWaitHide();
        this.processing = false;
    }

    async splitLine() {
        await this.save();
        const orderLineId = this.linesGrid.instance.getSelectedRowsData()[0].id;
        if (!orderLineId) return;

        const modalRef = this.modalService.show(OrderBuildDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                orderLineId: orderLineId,
                showAsDialog: true,
                storageKey: 'split-details-grid',
                title: 'Build Detail - Select Line to Edit',
                mode: 'split-line',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                const splitResult = await this.utility.splitOrderLinesBasedOnDetailEdit(result.orderLineDetailId);
                await alert(`The OrderLines have been split.  You may now edit options from the Sales Order screen.`, `${this.appInfo.settings.company}`);
                this.appInfo.pleaseWaitHide();
                await this.getData();
            }
            modalRef.hide();
        });
    }

    async addVINAsBuildDetail() {
        await this.save();
        const orderId = this.order.id;
        if (!orderId) return;
        const modalRef = this.modalService.show(OrderBuildDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                orderId: orderId,
                showAsDialog: true,
                storageKey: 'vin-detail-grid',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    addMiscLine(miscLine?: OrderLine) {
        const selectedRow = this.linesGrid.instance.getSelectedRowsData()[0];
        let line: any;
        let mode: string;
        if (selectedRow && selectedRow.isMisc) {
            line = selectedRow;
            mode = 'edit';
        } else {
            line = this.uow.manager.createEntity('OrderLine');
            this.setNewLineNumber(line);
            line.isMisc = true;
            line.isCredit = this.isCreditOrder;
            mode = 'add';
        }

        const modalRef = this.modalService.show(MiscLineDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                orderLine: line,
                order: this.order,
                isWarranty: this.order.typeId == OrderType.Types.Warranty,
                mode: mode,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.order.orderLines.push(result.orderLine);
                await this.save();
                this.getDataOrderLines();
                this.calcFreight();
                this.calcTotals();
            }
            modalRef.hide();
        });
    }

    async importFromInvoice() {
        if (!this.order.dealerId) return;

        const modalRef = this.modalService.show(ImportInvoiceLinesDialogComponent, {
            class: 'modal-xl',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dealerId: this.order.dealerId,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                result.invoiceLines.forEach((invoiceLine: InvoiceLine) => {
                    this.addCreditLineFromInvoiceLine(invoiceLine);
                });
                await this.save();
                this.getDataOrderLines();
                await this.save();
            }
            modalRef.hide();
        });
    }

    addCreditLineFromInvoiceLine(invoiceLine: InvoiceLine) {
        const part = invoiceLine.orderLineDetail?.orderLine?.part;
        const line = this.addOrderLine(part, invoiceLine.quantity, null, this.order, 'credit', invoiceLine.price);
        if (!part) {
            line.partNumber = invoiceLine.partNumber;
            line.description = invoiceLine.description;
        }
        line.returnedDetailId = invoiceLine.orderLineDetailId;
        return line;
    }

    async addPartLine() {
        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                columnLimitMode: true,
                showSideBar: true,
                salesOrderMode: true,
                storageKey: 'credit-order-part-search',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                const parts = (await this.uow.parts.byIds(result.partIds)) as Part[];
                result.partIds.forEach((partId) => {
                    const part = parts.filter((part) => part.id === partId)[0];
                    this.addOrderLine(part, 1, null, this.order, 'credit');
                });
                await this.save();
                this.getDataOrderLines();
                await this.save();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    verifyAllLinesNotBuilt(details): boolean {
        //check that all the details and options are not built
        const allDetailsNotBuilt = _.every(details, { built: null });
        let optionCheck = true;
        details.forEach((detail) => {
            detail.optionDetails.forEach((option) => {
                if (option.built) optionCheck = false;
            });
        });

        return allDetailsNotBuilt && optionCheck;
    }

    verifyAllLinesNotPaid(details): boolean {
        //check that all the details and options are not Paid
        const allDetailsNotPaid = details.filter((x) => x.paymentLines.length > 0).length === 0;
        let optionPaidCheck = true;
        details.forEach((detail) => {
            detail.optionDetails.forEach((option) => {
                if (option.paymentLines.length > 0) optionPaidCheck = false;
            });
        });
        return allDetailsNotPaid && optionPaidCheck;
    }

    async addLines(mode: any) {
        let submittedOrder = this.order.statusId !== 1 && this.order.statusId !== 6;
        if (submittedOrder) {
            const selectedLine = this.linesGrid.instance.getSelectedRowsData()[0];
            // if (!!selectedLine && mode === 'Options') {
            //     const details = (await this.uow.getOrderLineDetailsByOrderLineId(selectedLine.id, 'optionDetails.paymentLines, paymentLines')) as OrderLineDetail[];
            //     const builtCheck = this.verifyAllLinesNotBuilt(details);
            //     if (!builtCheck) {
            //         alert(`One or more of the details has been built already.  Must Add from the Build Detail screen`, this.appInfo.settings.company);
            //         return;
            //     }
            // }
        }

        let partTypeIds = [];
        let partOptions = [];
        const line = this.linesGrid.instance.getSelectedRowsData()[0];
        if (mode === 'Options' && !!line.part) {
            partOptions = ((await this.uow.getPartOptionsByPartId(line.part.id)) as any).map((x) => {
                return x.optionPart;
            });
        } else partOptions = null;
        partTypeIds = (await this.uow.partTypes.all())
            .filter((partType: any) => {
                return mode === 'Trailers'
                    ? partType.id === PartType.Types.Finished || partType.id === PartType.Types.Trailer
                    : mode === 'Replacements'
                      ? partType.id === PartType.Types.Replacement
                      : null;
            })
            .map((partType: any) => partType.id);
        // console.log('options', partOptions);
        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partTypeIds: partTypeIds,
                partOptions: partOptions,
                mode: mode,
                columnLimitMode: true,
                showSideBar: true,
                salesOrderMode: true,
                storageKey: `sales-order-part-search-${mode.toLowerCase()}`,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                if (submittedOrder) this.orderEditedAfterSubmit = true;

                this.appInfo.pleaseWaitShow();
                this.saving = true;
                const parts = (await this.uow.parts.byIds(result.partIds)) as Part[];
                result.partIds.forEach((partId) => {
                    const part = parts.filter((part) => part.id === partId)[0];
                    if (mode === 'Options') this.addOrderLine(part, line.quantity, line.id, this.order);
                    else this.addOrderLine(part, 1, null, this.order, mode);
                });
                await this.save();
                this.getDataOrderLines();
                await this.save(); //call save again to update tax information with new orderLines
                this.saving = false;
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    addOrderLine(part?: any, quantity?: number, parentLineId?: number, order?: any, mode?: any, priceOverride?: number, useMfgDesc?: boolean) {
        const line = this.uow.orderLines.createEntity() as OrderLine;
        line.order = order;
        if (part) {
            line.partId = part.id;
            line.partNumber = part.partNumber;
            line.description = useMfgDesc ? part.mfgDescription : part.description;
            line.partId = part.id;
            line.cost = part.cost;
            // line.price = this.isWarranty
            //     ? 0
            //     : this.order.typeId === Order.Types.RetailPartsOrder
            //     ? part.retailPrice
            //     : (this.order.pricingTierId > 1 && !part.tierPricingOverride)
            //     ? part.getTierPrice(this.order.pricingTierId)
            //     : part.price;
            line.originalPrice = part.price;
            line.isFreight = line.part.isFreight;
            line.tierPricingOverride = part.tierPricingOverride;
            line.prePaymentDiscountOverride = part.prePaymentDiscountOverride;
            line.isNonManufactured = part.isNonManufactured;
        }
        line.quantity = quantity;
        line.extendedQuantity = quantity;
        if (parentLineId) {
            line.parentLineId = parentLineId;
            line.isOption = true;
        }
        if (mode === 'Replacements') line.isReplacement = true;
        if (mode === 'credit') {
            line.isNonManufactured = true;
            line.isReplacement = true;
        }
        line.isCredit = this.isCreditOrder;
        this.setNewLineNumber(line);
        this.setLinePricing(line, priceOverride);
        return line;
    }
    setLinePricing(line: OrderLine, priceOverride?: number) {
        let price = 0;
        if (priceOverride) price = priceOverride;
        else if (this.isWarranty) price = 0;
        else if (this.order.typeId === Order.Types.RetailPartsOrder) price = line.part.retailPrice;
        else if (!!this.order.pricingTierId && !line.tierPricingOverride) price = line.part.getTierPrice(this.order.pricingTierId);
        else price = line.part?.retailPrice;
        line.price = price;
        line.priceAch = line.achLineAmount;

        if (this.isQuote) line.quotedPrice = price;
        return line;
    }

    setNewLineNumber(line: OrderLine) {
        if (line.parentLineId) {
            const existingLines = _.orderBy(
                this.order.orderLines.filter((l) => l.id === line.parentLineId || l.parentLineId === line.parentLineId),
                'lineNumber',
                'desc',
            );

            const nextLineNumber = existingLines[0].lineNumber + 1;
            this.order.orderLines.filter((l) => l.lineNumber >= nextLineNumber).forEach((l) => l.lineNumber++);
            line.lineNumber = nextLineNumber;
        } else {
            line.lineNumber = this.order.getNextLineNumber();
        }
    }

    createOrderLineDetail(orderLine: any, parentLineId?: number) {
        const detail = this.uow.orderLineDetails.createEntity() as OrderLineDetail;
        detail.statusId = OrderLineDetail.Statuses.New;
        detail.orderLineId = orderLine.id;
        detail.parentLineId = parentLineId;
        detail.isOption = orderLine.isOption;
        detail.isReplacement = orderLine.isReplacement;
        detail.isCredit = orderLine.isCredit;
        detail.isMisc = !orderLine.parentId && !orderLine.partId;
        detail.quantity = orderLine.isReplacement || (!orderLine.partId && !orderLine.isReplacement) ? orderLine.quantity : 1;
        detail.created = new Date();
        detail.deliveryOptionId = this.order.deliveryOptionId;
        detail.dueDate = this.order.dueDate;
        const buildLeadTime = this.orderLeadTime - this.buildLeadTime;
        detail.buildDue = this.order.dueDate ? moment(this.order.dueDate).add(-buildLeadTime, 'days').toDate() : moment(new Date()).add(this.buildLeadTime, 'days').toDate();
        // detail.scheduled = detail.buildDue; details are only scheduled from the Build Scheduler screen
        detail.vIN = !!orderLine.part ? orderLine.part.vINPrefix : null;
        detail.price = orderLine.price;
        detail.tax = Utility.calcLineTax(orderLine, orderLine.part?.partPartTypes, this.order.type);

        this.entitiesService.createOrderLineDetailNote(detail.id, 'Submitted', this.auth.currentUser.id);
        if (!!orderLine.note) this.entitiesService.createOrderLineDetailNote(detail.id, orderLine.note, this.auth.currentUser.id, 2, true);
        if (!!this.order.note) this.entitiesService.createOrderLineDetailNote(detail.id, this.order.note, this.auth.currentUser.id, 3, true);

        return detail;
    }

    async submit() {
        let emailDueDatePassed = false;
        let freightApprovalNote: string = null;

        if (!this.order.deliveryOptionId) {
            await alert('A delivery option needs to be selected before submitting', this.appInfo.settings.company);
            return;
        }

        if (!this.order.paymentMethod) {
            await alert('A payment method needs to be selected before submitting', this.appInfo.settings.company);
            return;
        }

        const deliveryOption = this.deliveryOptions?.find((x) => x.id === this.order.deliveryOptionId);
        const hasFreightLine = this.order.orderLines.some((line) => !line.deleted && line.part && line.part.isFreight);
        if (deliveryOption && deliveryOption.requiresFreight && !hasFreightLine) {
            await alert(
                'Genesis Delivery requires a Freight line. If free freight was agreed to, include a Freight line and set the price to $0.00.',
                this.appInfo.settings.company,
            );
            return;
        }

        // Check for zero priced freight approval
        if (deliveryOption && deliveryOption.requiresFreight && hasFreightLine) {
            const freightTotal = this.order.orderLines.filter((line) => !line.deleted && line.part && line.part.isFreight).reduce((sum, line) => sum + line.lineTotal, 0);
            if (freightTotal === 0) {
                const modalRef = this.modalService.show(InputDialogComponent, {
                    class: 'modal-sm',
                    initialState: {
                        dialogHeader: `${this.appInfo.settings.company}`,
                        dialogPrompt: 'Zero priced freight approved by:',
                        mode: 'text',
                    },
                });

                const promise = new Promise<boolean>(async (resolve, reject) => {
                    modalRef.content.result.subscribe(async (result) => {
                        modalRef.hide();
                        if (result.result) {
                            if (!result.value) {
                                alert('Zero dollar freight requires approval.', this.appInfo.settings.company);
                                resolve(false);
                            } else {
                                freightApprovalNote = `Zero dollar freight approved by: ${result.value}`;

                                resolve(true);
                            }
                        } else resolve(false);
                    });
                });
                const result = await promise;
                if (!result) return;
            }
        }

        if (this.order.deliveryOptionId === Order.DeliveryOptions.TBD) {
            const response = await confirm(`Are you sure you want Delivery Option set to TBD?`, `${this.appInfo.settings.company}`);
            if (!response) return;
        }

        // Check for dealer delivery option / sales order delivery option mismatch
        if (this.appInfo.getDealerDeliveryConfirmationEnabled() && !!this.order.dealerDeliveryOptionId && this.order.dealerDeliveryOptionId !== this.order.deliveryOptionId) {
            const modalRef = this.modalService.show(InputDialogComponent, {
                class: 'modal-sm',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    dialogHeader: 'Delivery Option Mismatch',
                    dialogPrompt: "Please provide a reason for submitting with a delivery option different than the dealer's request:",
                    mode: 'textarea',
                },
            });

            await new Promise<boolean>((resolve) => {
                modalRef.content.result.subscribe((result) => {
                    modalRef.hide();
                    if (!result.result) {
                        resolve(false);
                        return;
                    }
                    if (!result.value) {
                        alert("A reason is required to submit with a Delivery Option different than the dealer's request.", this.appInfo.settings.company);
                        resolve(false);
                        return;
                    }

                    const noteArray = [];
                    if (this.order.note && this.order.note.length > 0) noteArray.push(this.order.note);
                    const note = `Submitted with a delivery option different than the dealer's request for the following reason: ${result.value}`;
                    noteArray.push(note);
                    this.order.note = noteArray.join('\n');

                    resolve(true);
                });
            });
        }

        if (!!this.order.dueDate) {
            //Logic for checking it actually will cause passed build due dates: && moment(this.order.dueDate).add(-(this.orderLeadTime - this.buildLeadTime), 'days') < moment()
            const response = await confirm(
                "The order's due date was manually set. This may cause build due dates to be in the past. Are you sure you want to continue?",
                `${this.appInfo.settings.company}`,
            );
            if (!response) {
                return;
            } else {
                emailDueDatePassed = true;
            }
        }

        let hasAcknowledgedConfirmation = this.dealerConfirmations.some((x) => x.acknowledged);
        let containsTrailer = false;
        this.order.orderLines.forEach((x) => {
            if (x.part?.partPartTypes.some((y) => y.partTypeId === PartType.Types.Trailer)) containsTrailer = true;
        });
        if (this.order.typeId == Order.Types.SalesOrder && !hasAcknowledgedConfirmation && containsTrailer) {
            const modalRef = this.modalService.show(InputDialogComponent, {
                class: 'modal-sm',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    dialogHeader: 'No Acknowledged Confirmation',
                    dialogPrompt: 'Please provide a reason for submitting without an acknowledged confirmation:',
                    mode: 'textarea',
                },
            });

            const promise = new Promise<boolean>(async (resolve, reject) => {
                modalRef.content.result.subscribe(async (result) => {
                    modalRef.hide();
                    if (result.result) {
                        if (!result.value) {
                            alert('A reason is required to submit without an acknowledged confirmation.', this.appInfo.settings.company);
                            resolve(false);
                        } else {
                            const noteArray = [];
                            if (this.order.note && this.order.note.length > 0) noteArray.push(this.order.note);
                            const note = `Submitted without an acknowledged confirmation for the following reason: ${result.value}`;
                            noteArray.push(note);
                            this.order.note = noteArray.join('\n');

                            resolve(true);
                        }
                    }
                });
            });
            const awaitPromise = await promise;
            this.appInfo.pleaseWaitShow();
            await this.uow.commit();
            this.appInfo.pleaseWaitHide();
            if (!awaitPromise) return;
        }

        if (freightApprovalNote) {
            const noteArray = [];
            if (this.order.note && this.order.note.length > 0) noteArray.push(this.order.note);
            noteArray.push(freightApprovalNote);
            this.order.note = noteArray.join('\n');
        }

        const success = await Promise.resolve(this.save());
        if (!success) return;

        if (emailDueDatePassed && !this.showAsDialog) {
            const email = this.uow.emailMessages.createEntity() as EmailMessage;
            email.created = new Date();
            email.createdById = this.auth.currentUser.id;
            email.to = 'halferink@genesistrailers.com';
            email.subject = `Order #${this.order.invoiceNumber} Submitted With Manual Due Date`;
            email.body = `Order #${this.order.invoiceNumber} (Dealer: ${this.order.dealer.companyName}) was submitted by ${this.auth.currentUser.userName} with the due date manually set.`;
            await this.uow.commit();
            await this.utility.sendEmail(email.id);
        }

        this.appInfo.pleaseWaitShow();

        if (!this.order.dueDate) {
            const leadTime = ((await this.uow.getSettingsByName('LeadTime')) as Setting).intValue;
            this.order.dueDate = moment(new Date()).add(this.orderLeadTime, 'days').toDate();
        }

        //for the interim, set the ACH price to the calculated field
        this.order.orderLines.forEach((orderLine) => {
            orderLine.priceAch = orderLine.achLineAmount;
        });

        // create the trailers and replacements and miscs (lines with no parent id)
        const parentLines = this.order.orderLines
            .filter((orderLine) => {
                return !orderLine.parentLineId && !orderLine.submitted;
            })
            .map((x) => {
                return x;
            });

        const orderLineDetails = [];
        const today = new Date();

        for (let i = 0; i < parentLines.length; i++) {
            const orderLine = parentLines[i];

            // if replacement or misc make one line else make as many as quantity
            let loopQuantity = 1;
            if (!!orderLine.partId && !orderLine.isReplacement) loopQuantity = orderLine.quantity;
            for (let index = 0; index < loopQuantity; index++) {
                const detail = this.createOrderLineDetail(orderLine);
                orderLineDetails.push(detail);
                orderLine.submitted = today;
                // look for options under the parent orderLine
                const orderLineOptions = (await this.uow.getOptionsForOrderLine(orderLine.id)) as OrderLine[];

                if (orderLineOptions && orderLineOptions.length > 0)
                    for (let j = 0; j < orderLineOptions.length; j++) {
                        const optionLine = orderLineOptions[j];
                        optionLine.submitted = today;
                        const optionDetail = this.createOrderLineDetail(optionLine, detail.id);
                        orderLineDetails.push(optionDetail);
                    }
            }
        }

        // prevents creating orderLineDetailNotes if they change the note and then submit
        this.preSubmittedNote = this.order.note;
        this.order.statusId = Order.Statuses.InProcess;
        this.order.orderGuid = await this.uow.getGuid();
        await this.save();
        this.setPermissions();

        // call to utility to process the order line parts
        await this.utility.processOrderLineDetails(orderLineDetails.map((x) => x.id));
        // this.utility.submitOrder(this.order.id);
        this.appInfo.pleaseWaitHide();
    }

    async submitCredit() {
        if (!this.order.dealerId) {
            await alert('A dealer needs to be selected before submitting', this.appInfo.settings.company);
            return;
        }

        const success = await Promise.resolve(this.save());
        if (!success) return;

        this.appInfo.pleaseWaitShow();

        // TODO: implement this
        // for (const line of restockLines) {
        //     await this.restockCreditLine(line);
        // }

        this.uow.paymentCredits.createEntity({
            dealerId: this.order.dealerId,
            paymentCreditTypeId: PaymentCreditType.Types.Returns,
            fromPaymentId: null,
            appliedToPaymentId: null,
            creditAmount: this.order.total,
            availableAmount: this.order.total,
            creditOrderId: this.order.id,
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        });

        this.order.statusId = Order.Statuses.Complete;
        await this.uow.commit();
        this.setPermissions();
        this.appInfo.pleaseWaitHide();
    }

    async restockCreditLine(line: OrderLine) {
        await this.utility.restockCreditOrderLineDetail(line.returnedDetailId);
    }

    async submitSingleLine() {
        //only let them select parentLines
        const selectedRow = this.linesGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        const success = await Promise.resolve(this.save());
        if (!success) return;

        const result = await confirm('Are you sure you want to Submit these changes to this order.  This cannot be undone.  Continue?', `${this.appInfo.settings.company}`);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const orderLineDetails = [];
        const orderLine = selectedRow;

        const orderLineOptions = (await this.uow.getOptionsForOrderLine(orderLine.id)) as OrderLine[];
        const addedOptions = orderLineOptions.filter((x) => !x.submitted);

        // console.log('addedOptions', addedOptions);
        this.appInfo.pleaseWaitHide();
        // if replacement or misc make one line else make as many as quantity
        const today = new Date();
        if (!orderLine.submitted) {
            let loopQuantity = 1;
            if (!!orderLine.partId && !orderLine.isReplacement && !orderLine.isMisc) loopQuantity = orderLine.quantity;
            for (let index = 0; index < loopQuantity; index++) {
                //if the parentLine is new use the old logic
                const detail = this.createOrderLineDetail(orderLine);
                orderLineDetails.push(detail);
                // look for options under the parent orderLine
                orderLine.submitted = today;
                if (addedOptions && addedOptions.length > 0) {
                    for (let j = 0; j < addedOptions.length; j++) {
                        const optionLine = addedOptions[j];
                        optionLine.submitted = today;
                        const optionDetail = this.createOrderLineDetail(optionLine, detail.id);
                        orderLineDetails.push(optionDetail);
                    }
                }
            }
        } else {
            // have to get the orderLineDetails associated with the OrderLine
            const existingDetails = await this.uow.getOrderLineDetailsByOrderLineId(orderLine.id);
            // console.log('existingDetails', existingDetails);

            existingDetails.forEach((detail) => {
                for (let j = 0; j < addedOptions.length; j++) {
                    const optionLine = addedOptions[j];
                    optionLine.submitted = today;
                    const optionDetail = this.createOrderLineDetail(optionLine, detail.id);
                    orderLineDetails.push(optionDetail);
                }
            });
        }

        // save and call utility to process the order line parts
        await this.save();
        await this.utility.processOrderLineDetails(orderLineDetails.map((x) => x.id));
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    //edit sales order button
    async unSubmit() {
        //all orderline details need to be processed and have status New and have no paymentlines or packslips
        this.appInfo.pleaseWaitShow();
        const details = await this.uow.getOrderLineDetailByOrderId(this.order.id, 'orderLine, paymentLines');

        const built = _.some(details, (x) => x.built);
        if (built) {
            this.appInfo.pleaseWaitHide();
            await alert(`OrderLineDetail has been Built.  Cannot UnSubmit sales order.`, `${this.appInfo.settings.company}`);
            return;
        }

        const statusNew = _.every(details, (x) => x.statusId === OrderLineDetail.Statuses.New);
        if (!statusNew) {
            this.appInfo.pleaseWaitHide();
            await alert(`OrderLineDetail not on NEW status.  Cannot UnSubmit sales order.`, `${this.appInfo.settings.company}`);
            return;
        }

        const processed = _.every(details, (x) => x.processed);
        if (!processed) {
            this.appInfo.pleaseWaitHide();
            await alert(`OrderLineDetail has not been processed by Queue.`, `${this.appInfo.settings.company}`);
            return;
        }

        const noPayments = _.every(details, (x) => !x.paymentLines || x.paymentLines.length === 0 || _.every(x.paymentLines, (y) => y.deleted));
        if (!noPayments) {
            this.appInfo.pleaseWaitHide();
            await alert(`OrderLineDetail has a PaymentLines assigned.  Cannot UnSubmit sales order.`, `${this.appInfo.settings.company}`);
            return;
        }

        const noInvoiceLines = _.every(details, (x) => !x.invoiceLines || x.invoiceLines.length === 0 || _.every(x.invoiceLines, (y) => y.deleted));
        if (!noInvoiceLines) {
            this.appInfo.pleaseWaitHide();
            await alert(`OrderLineDetail has InvoiceLines assigned. Cannot UnSubmit sales order.`, `${this.appInfo.settings.company}`);
            return;
        }

        const noPackSlips = _.every(details, (x) => !x.packingSlipId);
        if (!noPackSlips) {
            this.appInfo.pleaseWaitHide();
            await alert(`OrderLineDetail has Packing Slip assigned. Cannot UnSubmit sales order.`, `${this.appInfo.settings.company}`);
            return;
        }
        const result = await (confirm('Are you sure you want to UnSubmit this order.  This cannot be undone.  Continue?', `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!result) return;

        if (statusNew && processed && noPayments && noPackSlips) {
            const orderLineDetailIds = details.map((x) => {
                return x.id;
            });
            //delete all the OrderLineDetailParts
            await this.utility.deleteOrderLineDetailPartsWhenSwitchingSalesOrderStatus(orderLineDetailIds);

            details.forEach((detail) => {
                this.entitiesService.createOrderLineDetailNote(detail.id, `Sales Order switched to New status via EditSalesOrder`, this.auth.currentUser.id);
                if (!detail.deleted) {
                    detail.deleted = new Date();
                }
            });
        }

        //clear the submitted date so the lines can be submitted again
        details.forEach((detail) => {
            if (!detail.orderLine.deleted) {
                detail.orderLine.submitted = null;
            }
        });

        this.order.statusId = Order.Statuses.New;
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async complete() {
        const result = await (confirm('Are you sure you want to COMPLETE this order?', `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!!result) {
            this.appInfo.pleaseWaitShow();
            this.order.statusId = Order.Statuses.Complete;
            await this.uow.commit();
            this.appInfo.pleaseWaitHide();
        }
    }

    async save() {
        const success = await Promise.resolve(this.validate());
        if (!success) return false;
        this.appInfo.pleaseWaitShow();
        this.isToastVisible = true;
        this.calcFreight();
        console.log('saveCalc');
        this.calcTotals();
        if (this.order.statusId !== Order.Statuses.New && this.preSubmittedNote !== this.order.note) {
            this.order.orderLines.forEach((orderLine) => {
                orderLine.orderLineDetails.forEach((orderLineDetail) => {
                    this.entitiesService.createOrderLineDetailNote(orderLineDetail.id, this.order.note, this.auth.currentUser.id);
                });
            });
        }
        await this.uow.commit();
        if (this.orderId === 'new' && !this.showAsDialog) {
            this.orderId = this.order.id;
            this.router.navigate(['/sales-orders/' + this.order.id], { replaceUrl: true });
            this.setTitle();
        }
        this.appInfo.pleaseWaitHide();
        return true;
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    async closeDialog(saveCredit?: boolean) {
        if (this.showAsDialog) {
            if (saveCredit) {
                await this.saveInvoiceCreditOrder();
            } else {
                this.uow.rollback();
                this.result.next({});
            }
        }
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    async partNumberGenerator(mode?: string) {
        mode = 'Trailers';
        let submittedOrder = this.order.statusId !== 1 && this.order.statusId !== 6;

        const modalRef = this.modalService.show(PartNumberDialogComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {},
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                if (submittedOrder) this.orderEditedAfterSubmit = true;

                this.appInfo.pleaseWaitShow();
                this.saving = true;
                const parts = (await this.uow.parts.byIds(result.selectedParts.map((part) => part.id))) as Part[];
                result.selectedParts
                    .filter((x) => !x.isOption)
                    .forEach((selectedPart) => {
                        const part = parts.filter((part) => part.id === selectedPart.id)[0];
                        const newLine = this.addOrderLine(part, 1, null, this.order, mode, null, true);
                        const optionsForPart = result.selectedParts.filter((x) => x.isOption && x.parentId === selectedPart.id);
                        optionsForPart.forEach((option) => {
                            const optionPart = parts.filter((part) => part.id === option.id)[0];
                            this.addOrderLine(optionPart, 1, newLine.id, this.order, 'Options', undefined, true);
                        });
                    });
                await this.save();
                this.getDataOrderLines();
                await this.save(); //call save again to update tax information with new orderLines
                this.saving = false;
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async email(mode?: string) {
        if (mode === 'confirmation' && this.appInfo.getDealerDeliveryConfirmationEnabled()) {
            const deliveryOption = this.deliveryOptions?.find((x) => x.id === this.order.deliveryOptionId);
            const hasFreightLine = this.order.orderLines.some((line) => !line.deleted && line.part && line.part.isFreight);
            if (deliveryOption && deliveryOption.requiresFreight && !hasFreightLine) {
                await alert(
                    'Genesis Delivery requires a Freight line. If free freight was agreed to, include a Freight line and set the price to $0.00.',
                    this.appInfo.settings.company,
                );
                return;
            }
        }

        const reSending = this.order.statusId === Order.Statuses.WaitingOnConfirmation;
        if (reSending) {
            const success = await confirm(`All open confirmations and ACHs will be deleted. Are you sure you want to re-send a confirmation?`, this.appInfo.settings.company);
            if (!success) return;
        }

        let body;
        if (this.isQuote) {
            body = ((await this.uow.getSettingsByName('QuoteEmailBody')) as any).stringValue;
        } else {
            body = ((await this.uow.getSettingsByName('SalesOrderEmailBody')) as any).stringValue;
        }

        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: mode === 'confirmation' ? 'modal-lg' : 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'SalesOrder',
                entityId: this.order.id,
                dealerEmail: this.order.dealer.email,
                body: body,
                emailConfirmation: mode === 'confirmation',
                order: this.order,
                isQuote: this.isQuote,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                const emailSent = await this.utility.emailReport(result.value);
                let ccEmailSent = true;
                if (result.ccEmailId) ccEmailSent = await this.utility.emailReport(result.ccEmailId);
                if (mode === 'confirmation') {
                    // if resending, delete existing confirmations
                    if (reSending) this.orderService.deleteConfirmations(this.confirmations, 'another confirmation was sent');
                    this.order.orderGuid = await this.uow.getGuid();
                    this.order.statusId = Order.Statuses.WaitingOnConfirmation;
                }
                await this.uow.commit();
                await this.getConfirmations();
                this.appInfo.pleaseWaitHide();
                await alert(emailSent && ccEmailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    async getOrderAttachments() {
        this.orderAttachments = await this.orderService.getAttachments(this.order?.id);
    }

    refreshAttachments() {
        this.orderAttachments = _(this.orderAttachments)
            .filter((a) => !a.deleted)
            .orderBy('name')
            .valueOf();
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    uploaded(e) {
        if (e && e.request && e.request.responseText) {
            const fileResponses = JSON.parse(e.request.responseText);
            console.log(fileResponses);
            for (let idx = 0; idx < fileResponses.length; idx++) {
                const fileResponse = fileResponses[idx];
                // create entity and attach to
                const orderAttachment = this.uow.orderAttachments.createEntity() as any;
                orderAttachment.order = this.order;
                orderAttachment.orderId = this.order.id;
                orderAttachment.name = fileResponse.name;
                orderAttachment.filename = fileResponse.filename;
                orderAttachment.created = new Date();
                orderAttachment.createdBy = this.auth.currentUser.userName;
                this.orderAttachments.push(orderAttachment);
            }
            this.refreshAttachments();
        }
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    openAttachment(attachment) {
        const fullPath = 'https://peccerium.blob.core.windows.net/genesis-trailers/order/attachments/';
        let fileName = attachment.filename.replace(fullPath, '');
        const fileNameURI = encodeURIComponent(fileName);
        const path = fullPath + fileNameURI;
        window.open(path, '_blank');
    }

    downloadAttachment(attachment) {
        console.log(attachment);
        const url = `${environment.serviceName}/file/downloadAttachment/?id=${attachment.id}`;
        window.open(url, '_blank');
    }

    removeAttachment(e, attachment) {
        e.preventDefault();
        attachment.deleted = new Date();
        this.refreshAttachments();
    }

    async auditLog(id?, entityTypeName?) {
        const ids = [];
        if (id) {
            ids.push(id);
        } else {
            ids.push(this.order.id);
        }
        const auditLogs = [];

        const orderAudits = await this.uow.getHistory('Order', [this.order.id]);
        orderAudits.forEach((x) => {
            auditLogs.push(x);
        });

        const orderLinesIds = (await this.uow.getOrderlinesByOrderId(this.order.id)).map((x: any) => {
            return x.id;
        });
        const orderDetailIds = await ((await this.uow.getOrderBuildDetailByOrderId(this.order.id)) as any).map((x) => {
            return x.id;
        });
        if (orderLinesIds && orderLinesIds.length > 0) {
            const audits = (await this.uow.getHistory('OrderLine', orderLinesIds)) as any;
            // console.log(audits);
            audits.forEach((x) => {
                auditLogs.push(x);
            });
        }
        if (orderDetailIds && orderDetailIds.length > 0) {
            const audits = (await this.uow.getHistory('OrderLineDetail', orderDetailIds)) as any;
            // console.log(audits);
            audits.forEach((x) => {
                x.entityTypeName = 'Build Detail';
                auditLogs.push(x);
            });
        }
        const modalRef = this.modalService.show(AuditDialogComponent, {
            class: 'modal-lg',
            initialState: {
                auditLogs: auditLogs,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
            }
            modalRef.hide();
        });
    }

    async unapplyAch() {
        const detailsWithPayments = await this.uow.getSalesOrderPaymentCheck(this.order.id);
        if (detailsWithPayments?.length > 0) {
            await alert(`There are payments applied to this Sales Order.  Cannot UnApply ACH pricing.`, `${this.appInfo.settings.company}`);
            return;
        }

        const result = await confirm(`Are you sure you want to UnApply the ACH Pricing?`, `${this.appInfo.settings.company}`);
        if (!result) return;

        const confirmationMessage = custom({
            title: `${this.appInfo.settings.company}`,
            messageHtml: `Do you want to undo the ACH pricing or remove the ACH transaction all together?`,
            buttons: [
                {
                    text: 'Undo Pricing',
                    onClick: (e) => {
                        return 'undo-pricing';
                    },
                },
                {
                    text: 'Remove Transaction',
                    onClick: (e) => {
                        return 'remove-transaction';
                    },
                },
                {
                    text: 'Cancel',
                    onClick: (e) => {
                        return 'cancel';
                    },
                },
            ],
        });
        const confirmation = await confirmationMessage.show();
        if (confirmation === 'cancel') return;

        this.appInfo.pleaseWaitShow();

        this.orderLines.forEach((line) => {
            line.paidAch = null;
        });
        const details = await this.uow.getOrderLineDetailByOrderId(this.order.id, 'orderLine');
        details.forEach((detail) => {
            detail.price = detail.orderLine.price;
        });

        if (confirmation === 'remove-transaction') {
            const achs = (await this.uow.getACHPaymentByOrderId(this.order.id)) as any;
            achs.forEach((ach) => {
                ach.deleted = new Date();
                ach.deletedBy = this.auth.currentUser.userName;
            });
            this.achInfo = null;
        }
        await this.save();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async applyAch() {
        await this.save();

        const modalRef = this.modalService.show(AchDialogComponent, {
            class: 'modal-sm',
            initialState: {
                order: this.order,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result && result.success) {
                this.appInfo.pleaseWaitShow();
                this.orderLines.forEach((line) => {
                    line.paidAch = new Date();
                });
                const details = await this.uow.getOrderLineDetailByOrderId(this.order.id, 'orderLine');
                details.forEach((detail) => {
                    detail.price = detail.orderLine.priceAch;
                });

                await this.save();
                await this.getData();
                this.linesGrid.instance.refresh();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async editDescription(orderLine: OrderLine) {
        if (!orderLine) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Edit Description',
                dialogValue: orderLine.description,
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) orderLine.description = result.value;
            modalRef.hide();
        });
    }

    async updateLineQuantity() {
        const selected = this.linesGrid.instance.getSelectedRowsData()[0];
        if (selected?.returnedDetailId) {
            alert(`Quantity for a line imported from an invoice is fixed to the original detail's quantity and cannot be changed.`, this.appInfo.settings.company);
            return;
        }

        let submittedOrder = this.order.statusId !== 1 && this.order.statusId !== 6;
        if (submittedOrder) {
            const selectedLine = this.linesGrid.instance.getSelectedRowsData()[0];
            if (!selectedLine) return;

            this.appInfo.pleaseWaitShow();
            const details = (await this.uow.getOrderLineDetailsByOrderLineId(selectedLine.id, 'optionDetails.paymentLines, paymentLines')) as OrderLineDetail[];
            const builtCheck = this.verifyAllLinesNotBuilt(details);
            if (!builtCheck) {
                this.appInfo.pleaseWaitHide();
                alert(`One or more of the details has been built already.  Cannot update quantity`, this.appInfo.settings.company);
                return;
            }
        }

        const selectedRow = this.linesGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        this.appInfo.pleaseWaitHide();
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            initialState: {
                dialogHeader: `${this.appInfo.settings.company}`,
                dialogPrompt: 'Quantity',
                dialogValue: selectedRow.quantity,
                checkNumeric: true,
                checkInteger: true,
                // minValue: 1,
                mode: 'number',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result && result.result) {
                this.appInfo.pleaseWaitShow();
                const children = this.orderLines.filter((x) => {
                    return x.parentLineId === selectedRow.id;
                });
                children.forEach((line) => {
                    line.quantity = result.value;
                });
                selectedRow.quantity = result.value;
                this.calcTotals();
                if (!this.showAsDialog) {
                    await this.save();
                    await this.getData();
                }
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async updateLinePrice() {
        const selectedRow = this.linesGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;
        const originalPrice = selectedRow.originalPrice;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            initialState: {
                dialogHeader: `${this.appInfo.settings.company}`,
                dialogPrompt: 'New Price',
                dialogValue: selectedRow.price,
                checkNumeric: true,
                checkInteger: false,
                mode: 'number',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result && result.result) {
                this.appInfo.pleaseWaitShow();
                selectedRow.price = result.value;
                selectedRow.priceAch = selectedRow.achLineAmount; // update the ACH price as well
                if (this.isQuote) selectedRow.quotedPrice = result.value;
                const details = await this.uow.getOrderLineDetailsByOrderLineId(selectedRow.id);
                details.forEach((detail) => {
                    detail.price = result.value;
                    detail.tax = Utility.calcLineTax(detail, selectedRow.part?.partPartTypes, this.order.type);
                });
                this.calcTotals();
                if (!this.showAsDialog) {
                    await this.save();
                    await this.getData();
                }
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async moveLine(line: OrderLine, direction: 'up' | 'down') {
        let peers: OrderLine[] = [];
        if (line.parentLineId) peers = this.orderLines.filter((l) => l.parentLineId === line.parentLineId);
        else peers = this.orderLines.filter((l) => !l.parentLineId);
        peers = _(peers)
            .orderBy((l) => l.lineNumber)
            .valueOf();

        const index = peers.indexOf(line);
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === peers.length - 1)) return;

        // determine which line is top and which is bottom, then swap them and all their children
        const peer = peers[index + (direction === 'up' ? -1 : 1)];
        const topLine = direction === 'up' ? peer : line;
        const bottomLine = direction === 'up' ? line : peer;

        let nextLineNumber = topLine.lineNumber;
        bottomLine.lineNumber = nextLineNumber;
        nextLineNumber++;
        const bottomChildren = _(this.orderLines.filter((l) => l.parentLineId === bottomLine.id))
            .orderBy('lineNumber')
            .valueOf();
        bottomChildren.forEach((child) => {
            child.lineNumber = nextLineNumber;
            nextLineNumber++;
        });
        topLine.lineNumber = nextLineNumber;
        nextLineNumber++;
        const topChildren = _(this.orderLines.filter((l) => l.parentLineId === topLine.id))
            .orderBy('lineNumber')
            .valueOf();
        topChildren.forEach((child) => {
            child.lineNumber = nextLineNumber;
            nextLineNumber++;
        });

        this.clearDisplayOrder();

        await this.linesGrid.instance.refresh();
    }

    async createInvoice() {
        this.invoiceService.createInvoice([this.order.id], 'salesOrder');
    }

    openConfirmation(code: Guid) {
        if (!code) return;
        this.router.navigate(['/confirmations/history'], { queryParams: { code } });
    }

    async emailMsrps() {
        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'SalesOrderMsrps',
                entityId: this.order.id,
                dealerEmail: this.order.dealer.email,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                const emailSent = await this.utility.emailReport(result.value);
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
                await alert(emailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    async changeSalesRep() {
        const salesReps = await this.uow.getSalesReps();

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Change Sales Rep',
                dialogPrompt: 'Select Sales Rep',
                selectionRequired: true,
                mode: 'selectWithId',
                dialogValueArray: salesReps,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value && result?.value !== this.order.salesRepId) {
                const salesRep = (await this.uow.salesReps.byId(result.value)) as SalesRep;
                this.order.salesRep = salesRep;
            }
            modalRef.hide();
        });
    }

    async saveInvoiceCreditOrder() {
        this.order.invoiceNumber = await this.utility.getNextSalesOrderNumber();
        this.orderService.addOrderNote(this.order.id, `Created as Credit Invoice for Invoice #${this.invoice.invoiceNumber}`);
        // await this.save();

        const selectedPaymentLineId = await this.selectPaymentLine();
        if (!selectedPaymentLineId) return;

        //credit a payment line for the amount of the order total
        const paymentLine = (await this.uow.paymentLines.byId(selectedPaymentLineId, 'payment')) as PaymentLine;
        if (!paymentLine) {
            alert('An error occurred trying to find the selected payment line.  Cannot continue.', this.appInfo.settings.company);
            return;
        }
        const payment = (await this.uow.payments.byId(paymentLine.paymentId, 'paymentLines')) as Payment;
        if (!payment) {
            alert('An error occurred trying to find the payment for the selected payment line.  Cannot continue.', this.appInfo.settings.company);
            return;
        }

        paymentLine.amount -= Math.abs(this.order.total);

        const newPaymentLine = this.uow.paymentLines.createEntity({
            paymentId: paymentLine.paymentId,
            lineNumber: Payment.getNextLineNumber(payment.paymentLines),
            description: `Overpayment from Credit for Invoice#${this.invoice.invoiceNumber}`,
            amount: Math.abs(this.order.total),
            notes: `Created on ${moment().format('MM/DD/YYYY hh:mm:ssa')} by ${this.auth.currentUser.userName} for Invoice#${this.invoice.invoiceNumber}`,

            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        }) as PaymentLine;

        //create a payment credit
        const paymentCredit = this.uow.paymentCredits.createEntity({
            dealerId: this.order.dealerId,
            paymentCreditTypeId: PaymentCreditType.Types.OverPayment, //refund to customer
            fromPaymentId: payment.id,
            appliedToPaymentId: null,
            creditAmount: Math.abs(this.order.total),
            availableAmount: Math.abs(this.order.total),
            quickBooksId: payment.quickBooksId,
            quickBooksLastExport: payment.quickBooksLastExport,
            notes: `Credit for Invoice#${this.invoice.invoiceNumber}`,
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        }) as PaymentCredit;

        await this.submit();

        this.result.next({ success: true, orderId: this.order.id });
    }

    async selectPaymentLine(): Promise<number | null> {
        //have them select with payment they are applying the credit to from the paymentLines
        const availablePaymentLines = this.invoice.paymentLines
            .filter((x) => !x.deleted && x.amount >= Math.abs(this.order.total))
            .map((x) => {
                return { id: x.id, name: `Payment #${x.id} - ${x.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}` };
            });

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Apply Credit To Payment',
                dialogPrompt: 'Please select the Payment you are applying this credit to:',
                mode: 'selectWithId',
                dialogValueArray: availablePaymentLines,
            },
        });
        const promise = new Promise<number | null>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.value) {
                    console.log(result);
                    resolve(result.value);
                } else {
                    reject(null);
                }
                modalRef.hide();
            });
        });
        return promise;
    }
}
